import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  MenuItem,
  Divider,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getTicketById, updateTicket } from '../services/TicketDeCaisseService';
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';
import LineItemsEditor from '../components/LineItemsEditor';
import { PAYMENT_STATUS, getPaymentStatus } from '../../../constants/paymentStatus';
import TICKET_TYPE, { getTicketTypeLabel, getTicketTypeColor } from '../../../constants/ticketTypes';

const TicketEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Get payment methods from Redux store
  const paymentMethodsFromStore = useSelector((state) => state.paymentMethod.paymentMethods) || [];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ticket, setTicket] = useState(null);

  // Form fields
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState(''); // ✅ New field
  const [ticketType, setTicketType] = useState(TICKET_TYPE.SALE); // ✅ New field
  const [pickupDate, setPickupDate] = useState(''); // ✅ New field
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [paidAmount, setPaidAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [discountType, setDiscountType] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [vatPercentage, setVatPercentage] = useState(21);
  const [lineItems, setLineItems] = useState([]);


  useEffect(() => {
  }, [paymentMethodId, paymentMethod]);

  useEffect(() => {
    fetchTicketDetails();
    
    // Fetch payment methods if not already loaded
    if (paymentMethodsFromStore.length === 0) {
      dispatch(fetchPaymentMethods());
    } else {
    }
  }, [id]);

  const fetchTicketDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getTicketById(id);

      // Handle different response structures
      let ticketData = null;
      if (response?.payload) {
        ticketData = response.payload;
      } else if (response?.data?.payload) {
        ticketData = response.data.payload;
      } else if (response?.data) {
        ticketData = response.data;
      } else {
        ticketData = response;
      }

      setTicket(ticketData);

      // ✅ FIX: Use only one source for line items - prefer LineItems over Items
      const items = ticketData.LineItems || ticketData.Items || [];
      setLineItems(items);

      // Populate form fields
      setCustomerName(ticketData.CustomerName || '');
      setCustomerPhone(ticketData.CustomerPhone || ''); // ✅ Load phone
      setTicketType(ticketData.TicketType || TICKET_TYPE.SALE); // ✅ Load ticket type
      setPickupDate(ticketData.PickupDate ? new Date(ticketData.PickupDate).toISOString().slice(0, 10) : ''); // ✅ Load pickup date
      // ✅ Load PaymentMethodId - find matching payment method from store
      const backendPaymentMethodId = ticketData.PaymentMethodId;
      
      // Find the payment method in the store that matches the backend ID
      const matchingPaymentMethod = paymentMethodsFromStore.find(
        pm => pm.id === backendPaymentMethodId
      );
      
      if (matchingPaymentMethod) {
        setPaymentMethodId(matchingPaymentMethod.id);
        setPaymentMethod(matchingPaymentMethod.name?.toUpperCase() || matchingPaymentMethod.name);
      } else {
        // If not found in store, still set the ID (payment methods might not be loaded yet)
        setPaymentMethodId(backendPaymentMethodId);
        setPaymentMethod(ticketData.PaymentMethod?.trim().toUpperCase());
      }
      
      // Verify it was set
      setTimeout(() => {
      }, 100);
      
      setPaidAmount(ticketData.PaidAmount?.toString() || '');
      setNotes(ticketData.Notes || '');
      setVatPercentage(ticketData.VATPercentage || 21);

      if (ticketData.DiscountAmount && ticketData.DiscountAmount > 0) {
        setDiscountType(ticketData.DiscountType || 'Fixed');
        setDiscountValue(
          ticketData.DiscountType?.toLowerCase() === 'percentage'
            ? ticketData.DiscountPercentage?.toString() || ''
            : ticketData.DiscountAmount?.toString() || ''
        );
        setDiscountReason(ticketData.DiscountReason || '');
      }
    } catch (error) {
      console.error('❌ Error fetching ticket details:', error);
      setError(error.message || 'Failed to load ticket details');
      toast.error(t('failed_to_load_ticket') || 'Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/tickets/view/${id}`);
  };

  const handleSave = async () => {
    try {
      
      setSaving(true);

      // ✅ Validate line items
      if (!lineItems || lineItems.length === 0) {
        toast.error(t('at_least_one_item_required') || 'At least one item is required');
        setSaving(false);
        return;
      }


      // Get base subtotal from line items (sum of line items before VAT and discount)
      const subtotal = lineItems.reduce((sum, item) => {
        const itemSubtotal = (item.Quantity || 1) * (item.UnitPriceExclTax || item.UnitPrice || 0);
        return sum + itemSubtotal;
      }, 0);

      // Calculate discount details
      const vat = parseFloat(vatPercentage) || 21;
      let discAmount = 0;
      let discPercentage = null;
      let discType = null;
      let discReason = null;

      if (discountType && discountValue) {
        discType = discountType;
        discReason = discountReason.trim() || null;
        
        if (discountType.toLowerCase() === 'percentage') {
          discPercentage = parseFloat(discountValue) || 0;
          discAmount = (subtotal * discPercentage) / 100;
        } else {
          discAmount = parseFloat(discountValue) || 0;
        }
      }

      // Calculate totals
      const subtotalAfterDiscount = subtotal - discAmount;
      const vatAmount = (subtotalAfterDiscount * vat) / 100;
      
      // ✅ Calculate totalAmountInclVAT using UnitPriceInclVat to preserve original pricing
      const totalAmountInclVAT = lineItems.reduce((sum, item) => {
        const unitPrice = item.UnitPriceInclVat || (item.UnitPriceExclTax * (1 + vat / 100));
        return sum + (item.Quantity || 1) * unitPrice;
      }, 0);
      
      const totalAmountInclVATAfterDiscount = Math.round((totalAmountInclVAT - discAmount) * 100) / 100;

      // Calculate change (for UI reference only, not sent to backend)
      const paid = parseFloat(paidAmount) || 0;

      // Recalculate line items with new VAT and discount
      const updatedLineItems = lineItems.map(item => {
        const quantity = parseFloat(item.Quantity) || 1;
        const unitPriceExclTax = parseFloat(item.UnitPriceExclTax || item.UnitPrice) || 0;
        const unitPriceInclVat = unitPriceExclTax * (1 + vat / 100);

        // Calculate item discount
        let itemDiscountPercentage = 0;
        let itemDiscountAmount = 0;
        if (discType && discType.toLowerCase() === 'percentage' && discPercentage) {
          itemDiscountPercentage = discPercentage;
        }

        // Calculate line item totals
        const lineSubtotal = quantity * unitPriceExclTax;
        const lineDiscountAmount = (lineSubtotal * itemDiscountPercentage) / 100;
        const lineSubtotalAfterDiscount = lineSubtotal - lineDiscountAmount;
        const lineVATAmount = (lineSubtotalAfterDiscount * vat) / 100;
        const lineTotalInclVAT = lineSubtotal + (lineSubtotal * vat / 100); // Before discount
        const lineTotalInclVATAfterDiscount = lineSubtotalAfterDiscount + lineVATAmount;

        return {
          ProductId: item.ProductId || item.Id,
          ServiceName: item.ServiceName || null, // ✅ Optional service name
          ServiceDescription: item.ServiceDescription || null, // ✅ Optional service description
          ProductSerial: item.ProductSerial || null, // ✅ Optional product serial
          Quantity: quantity,
          UnitPriceExclTax: Math.round(unitPriceExclTax * 100) / 100,
          UnitPriceInclVat: Math.round(unitPriceInclVat * 100) / 100,
          Discount: itemDiscountPercentage,
          DiscountAmount: Math.round(lineDiscountAmount * 100) / 100,
          VATAmount: Math.round(lineVATAmount * 100) / 100,
          Subtotal: Math.round(lineSubtotal * 100) / 100,
          TotalAmountInclVATAfterDiscount: Math.round(lineTotalInclVATAfterDiscount * 100) / 100,
          TotalAmountInclVAT: Math.round(lineTotalInclVAT * 100) / 100
        };
      });

      // ✅ Validation: Ensure payment method ID is set
      if (!paymentMethodId) {
        toast.error('Please select a payment method');
        return;
      }

      // ✅ Validation: Line items must exist
      if (!lineItems || lineItems.length === 0) {
        toast.error('Cannot save ticket without line items');
        return;
      }
debugger;
      // ✅ Calculate payment status based on paid amount and total
      const paymentStatus = getPaymentStatus(paid, totalAmountInclVATAfterDiscount);

      // Prepare complete UpdateTicketDto (similar to CreateTicketDto)
      const updateData = {
        Id: parseInt(id), // ✅ Include ticket ID
        TicketType: ticketType, // ✅ Include ticket type
        CustomerPhone: customerPhone.trim() || null, // ✅ Include customer phone
        PickupDate: ticketType === TICKET_TYPE.REPAIR && pickupDate ? new Date(pickupDate).toISOString() : null, // ✅ Include pickup date for repair tickets
        CustomerId: ticket.CustomerId || null,
        CustomerName: customerName.trim() || 'Walk-in Customer',
        PaymentMethodId: paymentMethodId, // ✅ Required field
        PaymentMethod: paymentMethod?.trim().toUpperCase() || null, // ✅ Deprecated field (for backward compatibility)
        Subtotal: Math.round(subtotal * 100) / 100,
        VATPercentage: vat,
        VATAmount: Math.round(vatAmount * 100) / 100,
        DiscountType: discType,
        DiscountPercentage: discPercentage,
        DiscountAmount: Math.round(discAmount * 100) / 100,
        DiscountReason: discReason,
        TotalAmountInclVATAfterDiscount: Math.round(totalAmountInclVATAfterDiscount * 100) / 100,
        TotalAmountInclVAT: Math.round(totalAmountInclVAT * 100) / 100,
        PaidAmount: paid > 0 ? Math.round(paid * 100) / 100 : null,
        // ✅ AmountInChange removed - backend no longer expects this field
        Notes: notes.trim() || '',
        LineItems: updatedLineItems,
        Status: paymentStatus, // ✅ Calculate status: Paid, Not Paid, or Partial Paid
      };

      
      // Verify PaymentMethodId is actually set
      if (!updateData.PaymentMethodId) {
        console.error('❌ PaymentMethodId is missing in DTO!');
        toast.error('Payment method is required');
        return;
      }

      await updateTicket(id, updateData);

      toast.success(t('ticket_updated_success') || 'Ticket updated successfully');
      navigate(`/tickets/view/${id}`);
    } catch (error) {
      console.error('❌ Error updating ticket:', error);
      toast.error(error.response?.data?.message || t('failed_to_update_ticket') || 'Failed to update ticket');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // ✅ Calculate live preview totals (preserve original UnitPriceInclVat to avoid rounding issues)
  const calculateLiveTotals = () => {
    // Use UnitPriceExclTax for base subtotal
    const subtotal = lineItems.reduce((sum, item) => {
      const itemSubtotal = (item.Quantity || 1) * (item.UnitPriceExclTax || item.UnitPrice || 0);
      return sum + itemSubtotal;
    }, 0);

    const vat = parseFloat(vatPercentage) || 21;
    let discAmount = 0;

    if (discountType && discountValue) {
      if (discountType.toLowerCase() === 'percentage') {
        const discPercentage = parseFloat(discountValue) || 0;
        discAmount = (subtotal * discPercentage) / 100;
      } else {
        discAmount = parseFloat(discountValue) || 0;
      }
    }

    const subtotalAfterDiscount = subtotal - discAmount;
    const vatAmount = (subtotalAfterDiscount * vat) / 100;
    
    // ✅ Use UnitPriceInclVat when available to preserve original pricing
    const totalInclBeforeDiscount = lineItems.reduce((sum, item) => {
      const unitPrice = item.UnitPriceInclVat || (item.UnitPriceExclTax * (1 + vat / 100));
      return sum + (item.Quantity || 1) * unitPrice;
    }, 0);
    
    const totalAmountInclVATAfterDiscount = Math.round((totalInclBeforeDiscount - discAmount) * 100) / 100;

    return {
      subtotal,
      discAmount,
      vatAmount,
      totalAmountInclVATAfterDiscount,
    };
  };

  const liveTotals = calculateLiveTotals();

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !ticket) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {error || t('ticket_not_found') || 'Ticket not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tickets')}
          sx={{ mt: 2 }}
        >
          {t('back_to_tickets') || 'Back to Tickets'}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} sx={{ bgcolor: '#f5f5f5' }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {t('edit_ticket') || 'Edit Ticket'}
                </Typography>
                <Chip
                  label={getTicketTypeLabel(ticketType, t)}
                  color={getTicketTypeColor(ticketType)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {ticket.TicketNumber}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={saving}
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? t('saving') || 'Saving...' : t('save_changes') || 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Ticket Summary (Read-only) */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {t('original_ticket_summary') || 'Original Ticket Summary'}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              {t('subtotal_excl_vat') || 'Subtotal (Excl. VAT)'}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatCurrency(ticket.Subtotal)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              {t('vat_amount') || 'VAT Amount'}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatCurrency(ticket.VATAmount)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              {t('total_items') || 'Total Items'}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {(ticket.LineItems || ticket.Items || []).length}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              {t('total_amount') || 'Total Amount'}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
              {formatCurrency(ticket.TotalAmountInclVATAfterDiscount || ticket.TotalAmountInclVAT)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Live Preview */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: '#e3f2fd', border: '2px solid', borderColor: 'primary.main' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          {t('new_totals_preview') || 'New Totals (Preview)'}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              {t('subtotal_excl_vat') || 'Subtotal (Excl. VAT)'}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {formatCurrency(liveTotals.subtotal)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              {t('vat_amount') || 'VAT Amount'} ({vatPercentage}%)
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {formatCurrency(liveTotals.vatAmount)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              {t('total_items') || 'Total Items'}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {lineItems.length}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              {t('discount') || 'Discount'}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: liveTotals.discAmount > 0 ? 'success.main' : 'text.secondary' }}>
              {liveTotals.discAmount > 0 ? `-${formatCurrency(liveTotals.discAmount)}` : '-'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1, border: '2px solid', borderColor: 'primary.main' }}>
              <Typography variant="caption" color="text.secondary">
                {t('grand_total') || 'GRAND TOTAL'}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.5 }}>
                {formatCurrency(liveTotals.totalAmountInclVATAfterDiscount)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Edit Form */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        {/* Customer Information Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: 'primary.main' }}>
            {ticketType === TICKET_TYPE.REPAIR ? (t('repair_information') || 'Repair Information') : (t('customer_information') || 'Customer Information')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            {ticketType === TICKET_TYPE.REPAIR 
              ? (t('repair_information_description') || 'Manage customer and repair details')
              : (t('customer_information_description') || 'Manage customer details for this sale')
            }
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Ticket Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('ticket_type') || 'Ticket Type'}</InputLabel>
                <Select
                  value={ticketType}
                  onChange={(e) => setTicketType(e.target.value)}
                  label={t('ticket_type') || 'Ticket Type'}
                >
                  <MenuItem value={TICKET_TYPE.SALE}>
                    {getTicketTypeLabel(TICKET_TYPE.SALE, t)}
                  </MenuItem>
                  <MenuItem value={TICKET_TYPE.REPAIR}>
                    {getTicketTypeLabel(TICKET_TYPE.REPAIR, t)}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Customer Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('customer_name') || 'Customer Name'}
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder={t('enter_customer_name') || 'Enter customer name'}
                helperText={t('customer_name_helper') || 'Leave empty for walk-in customer'}
              />
            </Grid>

            {/* Customer Phone (only for Repair Tickets) */}
            {ticketType === TICKET_TYPE.REPAIR && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('customer_phone') || 'Customer Phone'}
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder={t('enter_customer_phone') || 'Enter phone number'}
                  type="tel"
                  helperText={t('customer_phone_helper') || 'Contact number for pickup notification'}
                />
              </Grid>
            )}

            {/* Pickup Date (only for Repair Tickets) */}
            {ticketType === TICKET_TYPE.REPAIR && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('pickup_date') || 'Pickup Date'}
                  value={pickupDate}
                  onChange={(e) => setPickupDate(e.target.value)}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  helperText={t('pickup_date_helper') || 'Expected date for item pickup'}
                  required
                />
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Payment Information Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: 'success.main' }}>
            {t('payment_information') || 'Payment Information'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            {t('payment_information_description') || 'Manage payment details and pricing'}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Payment Method */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('payment_method') || 'Payment Method'}</InputLabel>
                <Select
                  value={paymentMethodId || ''}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedPm = paymentMethodsFromStore.find(pm => pm.id === selectedId);
                    setPaymentMethodId(selectedId);
                    setPaymentMethod(selectedPm?.name?.toUpperCase() || selectedPm?.name || '');
                  }}
                  label={t('payment_method') || 'Payment Method'}
                >
                  {paymentMethodsFromStore.length > 0 ? (
                    paymentMethodsFromStore.map((pm) => (
                      <MenuItem key={pm.id} value={pm.id}>
                        {pm.name?.toUpperCase() || pm.name}
                      </MenuItem>
                    ))
                  ) : (
                    [
                      <MenuItem key="NoPaymentMethod" value="">No Payment Method</MenuItem>,
                    ]
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Paid Amount */}
            <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label={t('paid_amount') || 'Paid Amount'}
              value={paidAmount}
              onChange={(e) => setPaidAmount(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          {/* VAT Percentage */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label={t('vat_percentage') || 'VAT Percentage'}
              value={vatPercentage}
              onChange={(e) => setVatPercentage(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              helperText={t('vat_percentage_helper') || 'Standard VAT rate for this ticket'}
            />
          </Grid>

          {/* Discount Section */}
          <Grid item xs={12}>
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, color: 'warning.main' }}>
                {t('discount') || 'Discount'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                {t('discount_description') || 'Apply a discount to this ticket'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>{t('discount_type') || 'Discount Type'}</InputLabel>
              <Select
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value)}
                label={t('discount_type') || 'Discount Type'}
              >
                <MenuItem value="">
                  <em>{t('no_discount') || 'No Discount'}</em>
                </MenuItem>
                <MenuItem value="Percentage">Percentage</MenuItem>
                <MenuItem value="Fixed">Fixed Amount</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {discountType && (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={discountType === 'Percentage' ? t('discount_percentage') || 'Percentage' : t('discount_amount') || 'Amount'}
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  InputProps={{
                    startAdornment: discountType === 'Fixed' && <InputAdornment position="start">€</InputAdornment>,
                    endAdornment: discountType === 'Percentage' && <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: discountType === 'Percentage' ? 1 : 0.01 }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('discount_reason') || 'Discount Reason'}
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  placeholder={t('optional') || 'Optional'}
                />
              </Grid>
            </>
          )}
          </Grid>
        </Box>

        {/* Additional Notes Section */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
            {t('additional_notes') || 'Additional Notes'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            {t('notes_section_description') || 'Add any additional information or instructions'}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {/* Notes */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('notes') || 'Notes'}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('enter_notes_placeholder') || 'Enter notes for this ticket...'}
            helperText={t('notes_helper') || 'Add any additional information about this ticket'}
          />
        </Box>
      </Paper>

      {/* Line Items Editor */}
      <Paper sx={{ p: 3, mt: 3, borderRadius: 2 }}>
        <LineItemsEditor
          lineItems={lineItems}
          onLineItemsChange={setLineItems}
          vatPercentage={parseFloat(vatPercentage) || 21}
        />
      </Paper>
    </Container>
  );
};

export default TicketEditPage;
